import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/SaveAlt';
import DifferenceIcon from '@mui/icons-material/Difference';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';

// SnapshotsPanel — sub-tab under the Analysis page that lets the
// operator save the current actuator-controllable mesh state, browse
// previously-saved snapshots, diff one against the live network, and
// re-apply only the drift via the existing analyser-action runner
// (`api.executeAnalyzerAction`). The diff endpoint already returns
// actions[] in the same shape every analyser emits, so re-apply
// reuses the exact same per-action POST/PATCH path used elsewhere
// in the Analysis page — no separate actuator code lives in this
// component.

const KIND_LABELS = {
  parent: 'Parent',
  white_list: 'Whitelist',
  channel: 'Channel',
  tx_power: 'TX power',
  missing_in_live: 'Missing in live',
  missing_in_snap: 'New since snapshot',
};

const KIND_COLORS = {
  parent: 'primary',
  white_list: 'default',
  channel: 'info',
  tx_power: 'warning',
  missing_in_live: 'error',
  missing_in_snap: 'default',
};

const ACTION_KIND_LABELS = {
  parent: 'Reparent',
  channel: 'Set channel',
  tx_power: 'Set TX power',
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const renderChangeValue = (change) => {
  if (change.kind === 'channel' || change.kind === 'tx_power') {
    return `${change.current_numeric ?? 0} → ${change.snapshot_numeric ?? 0}`;
  }
  if (change.kind === 'missing_in_live') return 'No live device';
  if (change.kind === 'missing_in_snap') return 'No snapshot baseline';
  const cur = change.current_value || '—';
  const snap = change.snapshot_value || '—';
  return `${cur} → ${snap}`;
};

const SnapshotsPanel = ({ onOpenPlan } = {}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [snapshots, setSnapshots] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');

  const [activeDiff, setActiveDiff] = useState(null);
  const [diffSnapshotId, setDiffSnapshotId] = useState(null);
  const [diffSnapshotName, setDiffSnapshotName] = useState('');
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyProgress, setApplyProgress] = useState({ done: 0, total: 0 });
  const [snapshotPersistTree, setSnapshotPersistTree] = useState(true);

  const [confirmDelete, setConfirmDelete] = useState(null);

  const refreshSnapshots = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await api.listTreeSnapshots();
      setSnapshots(Array.isArray(data) ? data : []);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to load snapshots', { variant: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { refreshSnapshots(); }, [refreshSnapshots]);

  const openSaveDialog = useCallback(() => {
    const def = `Snapshot ${new Date().toLocaleString()}`;
    setSaveName(def);
    setSaveDescription('');
    setSaveOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = saveName.trim();
    if (!name) {
      enqueueSnackbar('Snapshot name is required.', { variant: 'warning' });
      return;
    }
    setSavingSnapshot(true);
    try {
      const created = await api.createTreeSnapshot({
        name,
        description: saveDescription.trim() || undefined,
      });
      enqueueSnackbar(`Snapshot saved (${created?.device_count ?? 0} devices).`, { variant: 'success' });
      setSaveOpen(false);
      refreshSnapshots();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to save snapshot', { variant: 'error' });
    } finally {
      setSavingSnapshot(false);
    }
  }, [saveName, saveDescription, enqueueSnackbar, refreshSnapshots]);

  const openDiff = useCallback(async (snap) => {
    setDiffSnapshotId(snap.id);
    setDiffSnapshotName(snap.name);
    setActiveDiff(null);
    setLoadingDiff(true);
    try {
      const diff = await api.diffTreeSnapshot(snap.id);
      setActiveDiff(diff);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to compute diff', { variant: 'error' });
      setDiffSnapshotId(null);
    } finally {
      setLoadingDiff(false);
    }
  }, [enqueueSnackbar]);

  const closeDiff = useCallback(() => {
    if (applyBusy) return;
    setDiffSnapshotId(null);
    setActiveDiff(null);
  }, [applyBusy]);

  const refreshDiff = useCallback(async () => {
    if (!diffSnapshotId) return;
    setLoadingDiff(true);
    try {
      const diff = await api.diffTreeSnapshot(diffSnapshotId);
      setActiveDiff(diff);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to refresh diff', { variant: 'error' });
    } finally {
      setLoadingDiff(false);
    }
  }, [diffSnapshotId, enqueueSnackbar]);

  const applyAll = useCallback(async () => {
    const actions = activeDiff?.actions || [];
    if (actions.length === 0) return;
    setApplyBusy(true);
    setApplyProgress({ done: 0, total: actions.length });
    try {
      // Convert the snapshot-diff actions into the createPlanRun
      // payload shape. The backend will dispatch them, capture the
      // pre-apply state, and the operator can monitor live progress
      // in the Plans tab.
      const planActions = actions
        .map((a) => ({
          action_id: a.action_id,
          serial: a.serial || a.payload?.serial_number,
          kind: a.kind, // backend normalises further from endpoint
          endpoint: a.endpoint,
          method: a.method || 'POST',
          payload: a.payload || {},
          description: a.description,
        }))
        .filter((a) => a.serial && a.endpoint);
      if (planActions.length === 0) {
        enqueueSnackbar('No applicable snapshot actions to dispatch.', { variant: 'warning' });
        setApplyBusy(false);
        return;
      }
      const plan = await api.createPlanRun({
        name: `Snapshot re-apply: ${diffSnapshotName} (${new Date().toLocaleString()})`,
        source: 'snapshot-diff',
        actions: planActions,
        persist_tree: snapshotPersistTree,
      });
      enqueueSnackbar(
        `Snapshot re-apply: queued ${plan.actions?.length || planActions.length} action(s). Dispatching in the background; live progress is in the Plans tab.`,
        { variant: 'success' },
      );
      if (typeof onOpenPlan === 'function' && plan?.id) onOpenPlan(plan.id);
    } catch (err) {
      const data = err?.data || err?.response;
      if (err?.status === 409 || err?.statusCode === 409) {
        const planId = data?.plan_id;
        const serial = data?.serial;
        enqueueSnackbar(
          serial && planId
            ? `Another plan is already running for ${serial}. Open it from the Plans tab.`
            : 'Another plan is already running for one of the targeted devices.',
          { variant: 'warning' },
        );
        if (planId && typeof onOpenPlan === 'function') onOpenPlan(planId);
      } else {
        // eslint-disable-next-line no-console
        console.error('Snapshot re-apply failed', err);
        enqueueSnackbar(err?.message || 'Snapshot re-apply failed.', { variant: 'error' });
      }
    } finally {
      setApplyBusy(false);
      // Refresh the diff so the table reflects whatever the backend
      // already reconciled by the time the response landed.
      refreshDiff();
    }
  }, [activeDiff, diffSnapshotName, enqueueSnackbar, refreshDiff, onOpenPlan, snapshotPersistTree]);

  const handleDelete = useCallback(async (snap) => {
    setConfirmDelete(null);
    try {
      await api.deleteTreeSnapshot(snap.id);
      enqueueSnackbar('Snapshot deleted.', { variant: 'success' });
      refreshSnapshots();
      if (diffSnapshotId === snap.id) {
        setDiffSnapshotId(null);
        setActiveDiff(null);
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to delete snapshot', { variant: 'error' });
    }
  }, [enqueueSnackbar, refreshSnapshots, diffSnapshotId]);

  const summaryChips = useMemo(() => {
    if (!activeDiff?.summary) return null;
    const s = activeDiff.summary;
    const items = [
      { label: `Devices in snapshot: ${s.devices_in_snapshot}`, color: 'default' },
      { label: `Devices in live: ${s.devices_in_live}`, color: 'default' },
      { label: `Changed: ${s.devices_changed}`, color: s.devices_changed > 0 ? 'primary' : 'success' },
      { label: `Parent: ${s.parent_changes}`, color: s.parent_changes > 0 ? 'primary' : 'default' },
      { label: `Channel: ${s.channel_changes}`, color: s.channel_changes > 0 ? 'info' : 'default' },
      { label: `TX power: ${s.tx_power_changes}`, color: s.tx_power_changes > 0 ? 'warning' : 'default' },
      { label: `Whitelist: ${s.white_list_changes}`, color: s.white_list_changes > 0 ? 'default' : 'default' },
      { label: `Missing live: ${s.missing_in_live}`, color: s.missing_in_live > 0 ? 'error' : 'default' },
      { label: `New since snap: ${s.missing_in_snap}`, color: s.missing_in_snap > 0 ? 'default' : 'default' },
    ];
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {items.map((it) => (
          <Chip key={it.label} size="small" label={it.label} color={it.color} variant="outlined" />
        ))}
      </Stack>
    );
  }, [activeDiff]);

  return (
    <Box>
      {/* Header / save current */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Tree snapshots</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Save the current actuator-controllable mesh state (per-device parent + STA whitelist + per-radio
              channel/width/tx power) so you can later compare it with the live network and re-apply only the drift.
              Telemetry, Public-WiFi config, and non-jnet2 endpoints are excluded from snapshots by design.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh saved snapshot list">
              <span>
                <IconButton onClick={refreshSnapshots} disabled={loadingList}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={openSaveDialog}
              disabled={savingSnapshot}
            >
              Save current as snapshot
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Saved snapshots */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Saved snapshots</Typography>
          {loadingList && <CircularProgress size={18} />}
        </Stack>
        {(!snapshots || snapshots.length === 0) && !loadingList && (
          <Alert severity="info" variant="outlined">
            No saved snapshots yet. Take one after applying an analysis plan to create a baseline you can compare
            against later.
          </Alert>
        )}
        {snapshots.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Devices</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshots.map((snap) => (
                  <TableRow key={snap.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{snap.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {snap.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                        {snap.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{snap.device_count}</TableCell>
                    <TableCell>
                      <Tooltip title={formatTimestamp(snap.created_at)}>
                        <span>{formatRelative(snap.created_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{snap.created_by || '—'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<DifferenceIcon />}
                          onClick={() => openDiff(snap)}
                        >
                          Compare with current
                        </Button>
                        <Tooltip title="Delete snapshot">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDelete(snap)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Save dialog */}
      <Dialog open={saveOpen} onClose={() => !savingSnapshot && setSaveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save current tree as snapshot</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Captures every connected jnet2 device&apos;s parent, STA whitelist, and per-radio channel / channel
            width / TX power. Telemetry and Public-WiFi configuration are not included.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              autoFocus
              fullWidth
              required
            />
            <TextField
              label="Description (optional)"
              value={saveDescription}
              onChange={(e) => setSaveDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. Baseline after applying meta plan #4317"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} disabled={savingSnapshot}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={savingSnapshot ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={savingSnapshot || !saveName.trim()}
          >
            Save snapshot
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diff dialog */}
      <Dialog open={Boolean(diffSnapshotId)} onClose={closeDiff} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          Compare snapshot with current network
          <Typography variant="body2" color="text.secondary">
            Snapshot: <strong>{diffSnapshotName}</strong>
            {activeDiff?.snapshot_created_at && ` (taken ${formatRelative(activeDiff.snapshot_created_at)})`}
          </Typography>
          <IconButton
            onClick={closeDiff}
            disabled={applyBusy}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDiff && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Computing diff…</Typography>
            </Stack>
          )}
          {!loadingDiff && activeDiff && (
            <Box>
              <Box sx={{ mb: 2 }}>{summaryChips}</Box>
              {applyBusy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Applying actions: {applyProgress.done}/{applyProgress.total}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={applyProgress.total > 0 ? (applyProgress.done / applyProgress.total) * 100 : 0}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
              {(activeDiff.changes?.length || 0) === 0 ? (
                <Alert severity="success" variant="outlined">
                  Live network already matches this snapshot — nothing to re-apply.
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Changes</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Device</TableCell>
                          <TableCell>Kind</TableCell>
                          <TableCell>Radio</TableCell>
                          <TableCell>Band</TableCell>
                          <TableCell>Live → Snapshot</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeDiff.changes.map((c, idx) => (
                          <TableRow key={`${c.serial}-${c.kind}-${c.radio || ''}-${idx}`}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{c.serial}</TableCell>
                            <TableCell>
                              <Chip size="small" label={KIND_LABELS[c.kind] || c.kind} color={KIND_COLORS[c.kind] || 'default'} variant="outlined" />
                            </TableCell>
                            <TableCell>{c.radio || '—'}</TableCell>
                            <TableCell>{c.band || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{renderChangeValue(c)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Re-apply actions ({activeDiff.actions?.length || 0})
                  </Typography>
                  {(activeDiff.actions?.length || 0) === 0 ? (
                    <Alert severity="info" variant="outlined">
                      The drift listed above can&apos;t be reconciled by an actuator call (e.g. the live
                      network has devices the snapshot doesn&apos;t cover). Nothing to re-apply.
                    </Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Device</TableCell>
                            <TableCell>Kind</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Endpoint</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeDiff.actions.map((a) => (
                            <TableRow key={a.action_id}>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{a.serial}</TableCell>
                              <TableCell>
                                <Chip size="small" label={ACTION_KIND_LABELS[a.kind] || a.kind} variant="outlined" />
                              </TableCell>
                              <TableCell>{a.description || '—'}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontFamily: 'monospace', fontSize: 12 }}>
                                {a.method} {a.endpoint}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2 }}>
          <FormControlLabel
            sx={{ mr: 'auto', alignItems: 'flex-start', maxWidth: { xs: '100%', sm: 380 } }}
            disabled={applyBusy || loadingDiff}
            control={(
              <Checkbox
                size="small"
                checked={snapshotPersistTree}
                onChange={(e) => setSnapshotPersistTree(e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2" component="span">
                  Pin tree before re-apply
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Lock unchanged devices to their current parent before snapshot actions.
                </Typography>
              </Box>
            )}
          />
          <Button onClick={refreshDiff} disabled={loadingDiff || applyBusy} startIcon={<RefreshIcon />}>
            Recompute
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDiff} disabled={applyBusy}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={applyBusy ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            onClick={applyAll}
            disabled={applyBusy || loadingDiff || !(activeDiff?.actions?.length > 0)}
          >
            Re-apply diff ({activeDiff?.actions?.length || 0})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete snapshot?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{confirmDelete?.name}</strong> ({confirmDelete?.device_count ?? 0} devices) will be permanently
            removed. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnapshotsPanel;
